import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/components/ui/table";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Eye,
  Shield,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import API from "@core/api/api";
import { validateProductStockMovements, validateStockMovement } from '@features/products/services/productService';

export function StockValidationPanel({ productId }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // Validate stock movements mutation
  const validateStockMovementsMutation = useMutation({
    mutationFn: async () => {
      return await validateProductStockMovements(productId);
    },
    onSuccess: (data) => {
      setValidationResult(data);
      queryClient.invalidateQueries(["stockHistory", productId]);
      queryClient.invalidateQueries(["stockCalculation", productId]);
      toast.success(
        data.message || t("Stock movements validated successfully")
      );
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || t("Failed to validate stock movements")
      );
    },
  });

  // Validate specific movement mutation
  const validateMovementMutation = useMutation({
    mutationFn: async (movementId) => {
      return await validateStockMovement(movementId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["stockHistory", productId]);
      queryClient.invalidateQueries(["stockCalculation", productId]);
      toast.success(t("Movement validated successfully"));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || t("Failed to validate movement")
      );
    },
  });

  const handleValidate = () => {
    setIsValidating(true);
    validateStockMovementsMutation.mutate(undefined, {
      onSettled: () => {
        setIsValidating(false);
      },
    });
  };

  const handleValidateMovement = (movementId) => {
    validateMovementMutation.mutate(movementId);
  };

  const getValidationStatusColor = (isValid) => {
    return isValid ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";
  };

  const getValidationStatusIcon = (isValid) => {
    return isValid ? (
      <CheckCircle className="h-4 w-4 text-green-600" />
    ) : (
      <XCircle className="h-4 w-4 text-red-600" />
    );
  };

  const formatDiscrepancy = (discrepancy) => {
    const sign = discrepancy >= 0 ? "+" : "";
    return `${sign}${discrepancy}`;
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Shield className="h-4 w-4 mr-2" />
          {t("Validate Stock")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {t("Stock Validation")}
          </DialogTitle>
          <DialogDescription>
            {t("Validate and correct stock movements to ensure data integrity")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Validation Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t("Validation Controls")}
              </CardTitle>
              <CardDescription>
                {t("Run validation checks on stock movements")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Button
                  onClick={handleValidate}
                  disabled={isValidating}
                  className="flex items-center gap-2"
                >
                  {isValidating ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <AlertTriangle className="h-4 w-4" />
                  )}
                  {isValidating ? t("Validating...") : t("Run Validation")}
                </Button>
                {validationResult && (
                  <Badge
                    className={getValidationStatusColor(
                      validationResult.isValid
                    )}
                  >
                    {validationResult.isValid ? t("Valid") : t("Invalid")}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Validation Results */}
          {validationResult && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {getValidationStatusIcon(validationResult.isValid)}
                  {t("Validation Results")}
                </CardTitle>
                <CardDescription>{validationResult.message}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-4 border rounded-lg">
                    <p className="text-2xl font-bold">
                      {validationResult.totalMovements}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("Total Movements")}
                    </p>
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {validationResult.totalMovements -
                        (validationResult.discrepancies?.length || 0)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("Valid Movements")}
                    </p>
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <p className="text-2xl font-bold text-red-600">
                      {validationResult.discrepancies?.length || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("Discrepancies")}
                    </p>
                  </div>
                </div>

                {/* Discrepancies Table */}
                {validationResult.discrepancies &&
                  validationResult.discrepancies.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="font-medium text-red-800">
                        {t("Found Discrepancies")}
                      </h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t("Movement ID")}</TableHead>
                            <TableHead>{t("Expected")}</TableHead>
                            <TableHead>{t("Actual")}</TableHead>
                            <TableHead>{t("Discrepancy")}</TableHead>
                            <TableHead>{t("Status")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {validationResult.discrepancies.map((discrepancy) => (
                            <TableRow key={discrepancy.movementId}>
                              <TableCell className="font-mono">
                                {discrepancy.movementId}
                              </TableCell>
                              <TableCell className="font-mono">
                                {discrepancy.expected}
                              </TableCell>
                              <TableCell className="font-mono">
                                {discrepancy.actual}
                              </TableCell>
                              <TableCell className="font-mono">
                                <span
                                  className={
                                    discrepancy.discrepancy >= 0
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }
                                >
                                  {formatDiscrepancy(discrepancy.discrepancy)}
                                </span>
                              </TableCell>
                              <TableCell>
                                <Badge variant="destructive">
                                  {t("Needs Correction")}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                {/* Corrections Applied */}
                {validationResult.corrections &&
                  validationResult.corrections.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="font-medium text-green-800">
                        {t("Corrections Applied")}
                      </h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t("Movement ID")}</TableHead>
                            <TableHead>{t("Old Value")}</TableHead>
                            <TableHead>{t("New Value")}</TableHead>
                            <TableHead>{t("Corrected At")}</TableHead>
                            <TableHead>{t("Status")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {validationResult.corrections.map((correction) => (
                            <TableRow key={correction.movementId}>
                              <TableCell className="font-mono">
                                {correction.movementId}
                              </TableCell>
                              <TableCell className="font-mono">
                                {correction.oldRunningTotal}
                              </TableCell>
                              <TableCell className="font-mono">
                                {correction.newRunningTotal}
                              </TableCell>
                              <TableCell>
                                {new Date(
                                  correction.correctedAt
                                ).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                <Badge className="bg-green-100 text-green-800">
                                  {t("Corrected")}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
              </CardContent>
            </Card>
          )}

          {/* Validation Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t("Validation Tips")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="font-medium">{t("What is validated?")}</p>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "The system checks that running totals are calculated correctly and match expected values."
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">{t("Automatic corrections")}</p>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "When discrepancies are found, the system can automatically correct running totals."
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium">{t("Data integrity")}</p>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "Regular validation ensures your stock data remains accurate and reliable."
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
            {t("Close")}
          </Button>
          {validationResult && !validationResult.isValid && (
            <Button
              onClick={handleValidate}
              disabled={isValidating}
              variant="destructive"
            >
              {isValidating ? t("Correcting...") : t("Apply Corrections")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
